/**
 * Module-aware mobile planning nav — hides preview surfaces when workspace module is off.
 */
import { useMemo } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { useTimePhasedPlanningEnabled } from '../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import {
  PLANNING_TAB_ITEMS as BASE_TAB_ITEMS,
  PLANNING_DRAWER_SECTIONS as BASE_DRAWER_SECTIONS,
  planningDrawerExtras,
} from './planningNavConfig';
import { useAppArea } from '../lib/appAreas';
import { useAreaReports } from '../hooks/prioritization/useAreaReports';
import { useModuleGate } from '../lib/moduleGate';
import { MORE_SENTINEL, SEARCH_SENTINEL, type MobileNavItem, type MobileNavSection } from './navConfig';
import { useContributorExperience, useRoleMenu } from '../hooks/prioritization/useContributorExperience';
import { roleMenuHasLink } from '../lib/contributorNavProfiles';

const LAUNCH_CALENDAR_HREF = '/planning/launch-calendar';

function filterLaunchCalendar(items: MobileNavItem[]): MobileNavItem[] {
  return items
    .filter((item) => item.href !== LAUNCH_CALENDAR_HREF)
    .map((item) => {
      if (!item.submenu?.length) return item;
      return {
        ...item,
        submenu: item.submenu.filter((child) => child.href !== LAUNCH_CALENDAR_HREF),
      };
    });
}

export function usePlanningMobileNav(): {
  tabItems: MobileNavItem[];
  drawerSections: MobileNavSection[];
} {
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  // Same rule as the desktop Plans menu: a member on the role-based menu sees
  // only the Plans links the shared builder gives them (buildRoleMenu 'plans').
  const { useContributorShell } = useContributorExperience();
  const roleMenu = useRoleMenu('plans');
  // What the desktop Plans sidebar draws besides its menu: the open plan's
  // links, Reports and Decisions (the Reports module), Help (audit #39).
  const location = useLocation();
  const planMatch = useMatch('/planning/plans/:cycleId/*');
  const planMatchExact = useMatch('/planning/plans/:cycleId');
  const planId = (planMatch ?? planMatchExact)?.params?.cycleId ?? null;
  const reportsEnabled = useModuleGate('reports');
  const areaReports = useAreaReports(useAppArea(location.pathname));

  return useMemo(() => {
    const extras = planningDrawerExtras({
      planId,
      reports: reportsEnabled ? areaReports : null,
      allowed: (href) => !useContributorShell || roleMenuHasLink(roleMenu, href),
    });
    // The open plan first; Reports, Decisions and Help above Workspace (Search, Settings).
    const withExtras = (sections: MobileNavSection[]): MobileNavSection[] => {
      const workspaceAt = sections.findIndex((section) => section.label === 'Workspace');
      const at = workspaceAt === -1 ? sections.length : workspaceAt;
      return [
        ...(extras.plan ? [extras.plan] : []),
        ...sections.slice(0, at),
        ...extras.tail,
        ...sections.slice(at),
      ];
    };

    const base = timePhasedEnabled
      ? { tabItems: BASE_TAB_ITEMS, drawerSections: BASE_DRAWER_SECTIONS }
      : {
          tabItems: filterLaunchCalendar(BASE_TAB_ITEMS),
          drawerSections: BASE_DRAWER_SECTIONS.map((section) => ({
            ...section,
            items: section.items.filter((item) => item.href !== LAUNCH_CALENDAR_HREF),
          })),
        };
    if (!useContributorShell) return { ...base, drawerSections: withExtras(base.drawerSections) };

    // More, Search and Settings are not Plans links; they always stay.
    const keep = (item: MobileNavItem) =>
      item.href === MORE_SENTINEL || item.href === SEARCH_SENTINEL || item.href.startsWith('/prioritization/settings')
      || roleMenuHasLink(roleMenu, item.href);
    return {
      tabItems: base.tabItems.filter(keep),
      drawerSections: withExtras(
        base.drawerSections
          .map((section) => ({ ...section, items: section.items.filter(keep) }))
          .filter((section) => section.items.length > 0),
      ),
    };
  }, [timePhasedEnabled, useContributorShell, roleMenu, planId, reportsEnabled, areaReports]);
}
